import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button, Input } from '../ui';
import { useFuelCapture } from '../../hooks/useFuelCapture';
import { combustibleService } from '../../services/combustibleService';
import type { TipoFotoTurno, FotoTurnoResponse } from '../../types/combustible';

interface CapturaTurnoProps {
  reporteId: number;
  tipo: TipoFotoTurno;
  onSuccess: (foto: FotoTurnoResponse) => void;
  onCancel: () => void;
}

const CapturaTurno: React.FC<CapturaTurnoProps> = ({ reporteId, tipo, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const {
    selfieFile,
    evidenciaFile,
    capturarSelfie,
    capturarEvidencia,
    listo,
  } = useFuelCapture();

  const [horometroDeclarado, setHorometroDeclarado] = useState<string>('');
  const [nivelDeclarado, setNivelDeclarado] = useState<string>('');
  const [enviando, setEnviando] = useState(false);

  const tituloPorTipo: Record<TipoFotoTurno, string> = {
    INICIO_TURNO: t('fuel_control.capture.title_start'),
    FIN_TURNO: t('fuel_control.capture.title_end'),
    ABASTECIMIENTO: t('fuel_control.capture.title_supply'),
  };

  const selfiePreview = useMemo(() => selfieFile ? URL.createObjectURL(selfieFile) : null, [selfieFile]);
  const evidenciaPreview = useMemo(() => evidenciaFile ? URL.createObjectURL(evidenciaFile) : null, [evidenciaFile]);

  const handleConfirmar = async () => {
    if (!selfieFile || !evidenciaFile) return;
    setEnviando(true);
    try {
      const foto = await combustibleService.subirEvidencia(reporteId, {
        tipo,
        fotoSelfie: selfieFile,
        fotoEvidencia: evidenciaFile,
        horometroDeclarado: horometroDeclarado ? parseFloat(horometroDeclarado) : undefined,
        nivelDeclarado: nivelDeclarado ? parseFloat(nivelDeclarado) : undefined,
      });
      toast.success(t('fuel_control.capture.success'));
      onSuccess(foto);
    } catch (error: any) {
      const mensaje = error?.response?.data?.detail || error?.message || t('fuel_control.capture.error');
      toast.error(mensaje);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{tituloPorTipo[tipo]}</h3>
        <p className="text-sm text-gray-600 mt-1">{t('fuel_control.capture.instructions')}</p>
      </div>

      {/* Paso 1: selfie */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {t('fuel_control.capture.selfie_label')}
        </label>
        <div className="flex items-center gap-4">
          {selfiePreview ? (
            <img src={selfiePreview} alt="selfie" className="w-20 h-20 object-cover rounded-full border border-gray-300" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-100 border border-dashed border-gray-300" />
          )}
          <Button type="button" variant="secondary" onClick={capturarSelfie}>
            {selfieFile ? t('fuel_control.capture.retake_selfie') : t('fuel_control.capture.take_selfie')}
          </Button>
        </div>
      </div>

      {/* Paso 2: evidencia */}
      {selfieFile && (
        <div className="space-y-3 pt-2 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700">
            {t('fuel_control.capture.evidence_label')}
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              type="text"
              inputMode="decimal"
              label={t('fuel_control.capture.horometro')}
              placeholder="0.0"
              value={horometroDeclarado}
              onChange={(e) => setHorometroDeclarado(e.target.value.replace(',', '.'))}
            />
            <Input
              type="text"
              inputMode="decimal"
              label={t('fuel_control.capture.nivel')}
              placeholder="0-100"
              value={nivelDeclarado}
              onChange={(e) => setNivelDeclarado(e.target.value.replace(',', '.'))}
            />
          </div>

          <div className="flex items-center gap-4">
            {evidenciaPreview ? (
              <img src={evidenciaPreview} alt="evidencia" className="w-28 h-20 object-cover rounded border border-gray-300" />
            ) : (
              <div className="w-28 h-20 rounded bg-gray-100 border border-dashed border-gray-300" />
            )}
            <Button type="button" variant="secondary" onClick={capturarEvidencia}>
              {evidenciaFile ? t('fuel_control.capture.retake_evidence') : t('fuel_control.capture.take_evidence')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={enviando}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleConfirmar}
          disabled={!listo || enviando}
          loading={enviando}
        >
          {t('fuel_control.capture.confirm')}
        </Button>
      </div>
    </div>
  );
};

export default CapturaTurno;
